import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { arDigits, fmtLocaleNum } from "@/lib/utils";
import { useMarketAreas, findAreaBySlug, resolvePair, AREA_EVENT, PAIR_EVENT } from "@/lib/marketAreas";
import MarketActivity from "@/components/market/intel/MarketActivity";
import AreaCompare from "@/components/market/AreaCompare";
import MortgageCalculator from "@/components/forms/MortgageCalculator";
import LoanForm from "@/components/forms/LoanForm";
import HandoverCollection from "@/components/handover/HandoverCollection";
import FAQ from "@/components/faq/FAQ";
import ConsultationForm from "@/components/forms/ConsultationForm";
import RoniCTACard from "@/components/marketing/RoniCTACard";

// Shared hero + Helmet shell for every landing page.
// Each specific page below wraps a re-used homepage component with:
//   • unique <title> + description + canonical (Helmet)
//   • distinct H1 + intro paragraph (avoids duplicate-content dedupe)
//   • Consultation form + FAQ at the bottom for internal linking + conversion
function LandingShell({ helmet, kicker, h1, intro, children, extraJsonLd, showConsultation = true, faqVariant = "market", compactMobile = false, wide = false }) {
  // If the route carries a deep-link param (`/dubai-market/:slug` or
  // `/dubai-compare-areas/:pair`), the child component (MarketWidgets /
  // AreaCompare) emits its OWN per-slug/per-pair canonical + hreflang.
  // We must NOT emit the parent (index-page) canonical + hreflang here,
  // because Helmet would conflict on the same `<link rel="canonical">`
  // and Google would see mismatched signals — every deep-link URL would
  // resolve to the index canonical and get dropped from the search index.
  const { slug, pair } = useParams();
  const isDeepLink = Boolean(slug || pair);
  // Derive EN + AR twins from the canonical (strip any query string) so every
  // page emits the full bidirectional hreflang triad Google needs.
  const enUrl = helmet.canonical.split("?")[0];
  const arUrl = `${enUrl}?lang=ar`;
  return (
    <>
      <Helmet>
        {!isDeepLink && <title>{helmet.title}</title>}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        {!isDeepLink && <meta name="description" content={helmet.description} />}
        {!isDeepLink && <link rel="canonical" href={helmet.canonical} />}
        {!isDeepLink && <link rel="alternate" hrefLang="en" href={enUrl} />}
        {!isDeepLink && <link rel="alternate" hrefLang="ar" href={arUrl} />}
        {!isDeepLink && <link rel="alternate" hrefLang="x-default" href={enUrl} />}
        {!isDeepLink && <meta property="og:type" content="website" />}
        {!isDeepLink && <meta property="og:title" content={helmet.title} />}
        {!isDeepLink && <meta property="og:description" content={helmet.description} />}
        {!isDeepLink && <meta property="og:url" content={helmet.canonical} />}
        <meta property="og:image" content="https://thepropertic.ae/og-share-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        {!isDeepLink && <meta name="twitter:title" content={helmet.title} />}
        {!isDeepLink && <meta name="twitter:description" content={helmet.description} />}
        {extraJsonLd && !isDeepLink && <script type="application/ld+json">{JSON.stringify(extraJsonLd)}</script>}
      </Helmet>
      <section className={`${compactMobile ? "pt-4 pb-3" : "pt-6 pb-6"} sm:pt-32 sm:pb-10 relative overflow-hidden`}>
        <div className="blob-gold w-[500px] h-[500px] -top-40 -end-40 opacity-40" />
        <div className={`mx-auto ${wide ? "max-w-[1480px] px-3 sm:px-6 lg:px-8" : "max-w-[1100px] px-6 lg:px-10"} relative`}>
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3"
          >{kicker}</motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="font-editorial text-3xl sm:text-4xl lg:text-5xl text-propertic-navy leading-[1.15]"
          >{h1}</motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className={`${compactMobile ? "mt-3 text-[13.5px] leading-[1.6] line-clamp-3 sm:line-clamp-none sm:mt-5 sm:leading-[1.75]" : "mt-5 text-[15px] leading-[1.75]"} text-propertic-ink-2/95 sm:text-base max-w-3xl lg:max-w-none`}
          >{intro}</motion.p>
        </div>
      </section>
      {children}
      {showConsultation && <ConsultationForm />}
      <FAQ variant={faqVariant} />
    </>
  );
}

// ============================================================================
// 1. /dubai-market , Dubai property market intelligence page
// ============================================================================
export function DubaiMarketPage() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const { slug } = useParams();
  const { areas, loaded } = useMarketAreas();
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    const on = (e) => setPicked(e.detail);
    window.addEventListener(AREA_EVENT, on);
    return () => window.removeEventListener(AREA_EVENT, on);
  }, []);
  const slugArea = slug ? (picked || findAreaBySlug(areas, slug)) : null;
  const slugMissing = Boolean(slug && loaded && areas.length && !slugArea);
  const areaH1 = slugArea
    ? (isAR
        ? <>بيانات سوق <span className="font-italic-serif text-propertic-gold">{slugArea.area_ar || slugArea.area}</span> العقاري.</>
        : <>{slugArea.area} <span className="font-italic-serif text-propertic-gold">property market data.</span></>)
    : slugMissing
      ? (isAR ? <>المنطقة غير موجودة.</> : <>Community not found.</>)
      : null;
  // Live YTD summary from DLD Open Data, replaces the previous static
  // "H1 2026 closed at AED 286.44B / 86,005 tx (DLD press release, July 2026)"
  // intro. Data-through week is included so the reader knows the freshness.
  const [ytd, setYtd] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/dld-test/summary")
      .then((r) => { if (!cancelled) setYtd(r.data); })
      .catch(() => { /* fall through to static fallback below */ });
    return () => { cancelled = true; };
  }, []);
  // Fallback numbers if the summary endpoint is unreachable — kept in
  // sync with the last audited DLD H1-2026 press release so the page
  // never displays "—".
  const salesBn = ytd?.ytd?.sales_aed_bn;
  const salesCount = ytd?.ytd?.sales_count;
  const dataThrough = ytd?.window?.iso_week_end || ytd?.window?.end_date;
  const introEN = salesBn && salesCount
    ? `2026 YTD in Dubai stands at AED ${salesBn.toFixed(2)} billion in property sales across ${salesCount.toLocaleString()} transactions${dataThrough ? ` (Dubai Land Department, data through ${dataThrough})` : ""}. Below is our breakdown of 44 major freehold communities with AED/sqft benchmarks and transaction volumes from the Dubai Land Department, plus gross rental yields that use the source and methodology shown beside each metric, zero projections.`
    : "H1 2026 closed at AED 286.44 billion in Dubai property sales across 86,005 transactions (source: Dubai Land Department official press release, July 2026). Below is our breakdown of 44 major freehold communities with AED/sqft benchmarks and transaction volumes from the Dubai Land Department, plus gross rental yields that use the source and methodology shown beside each metric.";
  const introAR = salesBn && salesCount
    ? `بلغ إجمالي مبيعات العقارات في دبي حتى اليوم من عام ٢٠٢٦ نحو \u2066${arDigits(salesBn.toFixed(2))}\u2069 مليار درهم عبر ${fmtLocaleNum(salesCount, true)} صفقة${dataThrough ? ` (دائرة الأراضي والأملاك، البيانات حتى ${arDigits(String(dataThrough))})` : ""}. نعرض أدناه توزيع ٤٤ منطقة تملّك حرّ رئيسية مع أسعار القدم المربع، حجم الصفقات من دائرة الأراضي والأملاك، والعائد الإيجاري وفق المصدر والمنهجية الموضّحين بجانب كل مؤشر، بدون أي تقدير أو استقراء.`
    : "H1 2026 أغلق على 286.44 مليار درهم من مبيعات العقارات في دبي عبر 86,005 صفقة (المصدر: البيان الرسمي لدائرة الأراضي والأملاك، يوليو 2026). نعرض أدناه توزيع 44 منطقة تملّك حرّ رئيسية مع أسعار القدم المربع، حجم الصفقات من دائرة الأراضي والأملاك، والعائد الإيجاري وفق المصدر والمنهجية الموضّحين بجانب كل مؤشر.";
  return (
    <LandingShell
      compactMobile
      wide
      helmet={{
        title: isAR
          ? "بيانات سوق دبي العقاري ٢٠٢٦ | الأسعار والصفقات والعوائد"
          : "Dubai Property Market Data 2026 | DLD Prices, Sales & Yields",
        description: isAR
          ? "استكشف بيانات سوق دبي العقاري مباشرةً من دائرة الأراضي، تشمل السعر لكل قدم مربّع والمبيعات والصفقات والعوائد ونسب قيد الإنشاء مقابل الجاهز عبر ٤٤ منطقة."
          : "Explore live Dubai property market data from DLD, including prices per sq ft, sales, transactions, yields and off-plan vs ready trends across 44 areas.",
        canonical: isAR ? "https://thepropertic.ae/dubai-market?lang=ar" : "https://thepropertic.ae/dubai-market",
      }}
      kicker={isAR ? "بيانات السوق · تُحدَّث أسبوعياً" : "Market data · Refreshed weekly"}
      h1={areaH1 || (isAR ? <>بيانات سوق دبي <span className="font-italic-serif text-propertic-gold">العقاري.</span></> : <>Dubai property <span className="font-italic-serif text-propertic-gold">market data.</span></>)}
      intro={isAR ? introAR : introEN}
      extraJsonLd={{
        "@context": "https://schema.org", "@type": "WebPage",
        "name": "Dubai Property Market · Weekly DLD Data",
        "url": "https://thepropertic.ae/dubai-market",
        "about": { "@type": "Place", "name": "Dubai", "containedInPlace": { "@type": "Country", "name": "United Arab Emirates" } },
        "isPartOf": { "@id": "https://thepropertic.ae/#website" }
      }}
    >
      <MarketActivity />
      <RoniCTACard
        testId="market-roni-cta"
        copy={{
          en: {
            eyebrow: "Ask Roni · AI Advisor",
            heading: "Understand what these numbers mean",
            headingAccent: "for your investment.",
            body: "Want to understand what these market numbers mean for your investment? Ask Roni, The Propertic AI Advisor, for a quick evidence-based answer, or submit the consultation form below for free advisory guidance.",
            cta: "Ask Roni",
          },
          ar: {
            eyebrow: "اسأل روني · مستشار ذكي",
            heading: "افهم ماذا تعني هذه الأرقام",
            headingAccent: "لاستثمارك.",
            body: "تريد أن تعرف ماذا تعني هذه الأرقام لاستثمارك؟ اسأل روني، المستشار الذكي من بروبيرتك، للحصول على إجابة سريعة مبنية على البيانات، أو اترك بياناتك في نموذج الاستشارة أدناه للحصول على توجيه مجاني من فريقنا.",
            cta: "اسأل روني",
          },
        }}
      />
    </LandingShell>
  );
}

// ============================================================================
// 2. /dubai-compare-areas , Community comparison tool
// ============================================================================
export function DubaiCompareAreasPage() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const { pair } = useParams();
  const { areas, loaded } = useMarketAreas();
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    const on = (e) => {
      const [a, b] = e.detail || [];
      setPicked([areas.find((x) => x.area === a), areas.find((x) => x.area === b)].filter(Boolean));
    };
    window.addEventListener(PAIR_EVENT, on);
    return () => window.removeEventListener(PAIR_EVENT, on);
  }, [areas]);
  const pairAreas = pair ? ((picked && picked.length === 2 && picked) || resolvePair(areas, pair)) : null;
  const pairMissing = Boolean(pair && loaded && areas.length && !pairAreas);
  const nm = (a) => (isAR ? a.area_ar || a.area : a.area);
  const pairH1 = pairAreas
    ? (isAR
        ? <>مقارنة {nm(pairAreas[0])} <span className="font-italic-serif text-propertic-gold">و{nm(pairAreas[1])}.</span></>
        : <>{nm(pairAreas[0])} vs <span className="font-italic-serif text-propertic-gold">{nm(pairAreas[1])}.</span></>)
    : pairMissing
      ? (isAR ? <>المقارنة غير موجودة.</> : <>Comparison not found.</>)
      : null;
  return (
    <LandingShell
      wide
      helmet={{
        title: isAR
          ? "قارن مناطق دبي العقارية | الأسعار والعوائد والمبيعات"
          : "Compare Dubai Property Areas | Prices, Yields & Sales",
        description: isAR
          ? "قارن ٤٤ منطقة عقارية في دبي بالاعتماد على بيانات دائرة الأراضي المباشرة: السعر لكل قدم، عوائد الإيجار، الصفقات، اتجاهات المبيعات، ونشاط السوق."
          : "Compare 44 Dubai property areas using live DLD data, including price per sq ft, rental yields, transactions, sales trends and market activity.",
        canonical: isAR ? "https://thepropertic.ae/dubai-compare-areas?lang=ar" : "https://thepropertic.ae/dubai-compare-areas",
      }}
      kicker={isAR ? "أداة مقارنة · ٤٤ منطقة" : "Comparison tool · 44 areas"}
      h1={pairH1 || (isAR ? <>قارن <span className="font-italic-serif text-propertic-gold">مناطق دبي</span> العقارية.</> : <>Compare Dubai <span className="font-italic-serif text-propertic-gold">property areas.</span></>)}
      intro={isAR
        ? "اختر أي منطقتين من مناطق دبي الحرّة الـ٤٤، من مرسى دبي إلى وسط المدينة، من النخلة إلى تلال دبي، من JVC إلى إعمار بيتشفرونت، لتقارن سعر القدم المربع، عائد الإيجار الإجمالي، عدد الصفقات منذ بداية ٢٠٢٦ حتى اليوم، ونسبة المبيعات قيد الإنشاء مقابل السوق الجاهز. سجلات الصفقات من دائرة الأراضي والأملاك بدبي، أما مؤشرات الإيجار والعائد فتعتمد المصدر والمنهجية الموضّحين بجانب كل مؤشر."
        : "Pick any two of Dubai's 44 major freehold communities, Dubai Marina, Downtown Dubai, Palm Jumeirah, Business Bay, Jumeirah Village Circle (JVC), Dubai Hills Estate, Emaar Beachfront, MBR City and 36 more, and compare AED/sqft, gross rental yield, YTD 2026 transaction count and off-plan vs ready split. Transaction records are sourced from the Dubai Land Department; rental and yield metrics use the source and methodology shown beside each metric."}
      extraJsonLd={{
        "@context": "https://schema.org", "@type": "WebPage",
        "name": "Compare Dubai Communities Side-by-Side",
        "url": "https://thepropertic.ae/dubai-compare-areas",
        "about": [
          { "@type": "Place", "name": "Dubai Marina" }, { "@type": "Place", "name": "Downtown Dubai" },
          { "@type": "Place", "name": "Palm Jumeirah" }, { "@type": "Place", "name": "Business Bay" },
          { "@type": "Place", "name": "Jumeirah Village Circle" }, { "@type": "Place", "name": "Dubai Hills Estate" },
          { "@type": "Place", "name": "Emaar Beachfront" }, { "@type": "Place", "name": "Dubai Creek Harbour" }
        ]
      }}
    >
      <AreaCompare />
      <RoniCTACard
        testId="compare-roni-cta"
        copy={{
          en: {
            eyebrow: "Ask Roni · AI Advisor",
            heading: "Which area fits",
            headingAccent: "your goals?",
            body: "Not sure which area fits your goals? Ask Roni, The Propertic AI Advisor, to help you interpret the differences, or submit the consultation form below for a free, tailored advisory.",
            cta: "Ask Roni",
          },
          ar: {
            eyebrow: "اسأل روني · مستشار ذكي",
            heading: "أي منطقة تناسب",
            headingAccent: "أهدافك؟",
            body: "لست متأكداً أي منطقة تناسب أهدافك؟ اسأل روني، المستشار الذكي من بروبيرتك، ليساعدك في تفسير الفوارق بين المناطق، أو اترك بياناتك في نموذج الاستشارة أدناه للحصول على استشارة مجانية مخصّصة.",
            cta: "اسأل روني",
          },
        }}
      />
    </LandingShell>
  );
}

// ============================================================================
// 3. /mortgage-calculator , the tool itself
// ============================================================================
export function MortgageCalculatorPage() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const loanRef = useRef();
  return (
    <LandingShell
      wide
      showConsultation={false}
      faqVariant="mortgage"
      helmet={{
        title: isAR
          ? "حاسبة الرهن العقاري في دبي | الأقساط ونسبة القرض والتكاليف"
          : "Dubai Mortgage Calculator | Payments, LTV & Costs",
        description: isAR
          ? "احسب أقساط الرهن العقاري في الإمارات، الدفعة الأولى، تكاليف التمويل، وسيناريوهات نسبة القرض إلى القيمة. قارن المدخلات بالدرهم والدولار بحسابات توضيحية واضحة."
          : "Estimate UAE mortgage payments, down payment, financing costs and LTV scenarios. Compare AED or USD inputs with clear illustrative calculations.",
        canonical: isAR ? "https://thepropertic.ae/mortgage-calculator?lang=ar" : "https://thepropertic.ae/mortgage-calculator",
      }}
      kicker={isAR ? "أداة · متوافقة مع CBUAE" : "Tool · CBUAE-aligned"}
      h1={isAR ? <>احسب <span className="font-italic-serif text-propertic-gold">قسط الرهن</span> العقاري.</> : <>Calculate your <span className="font-italic-serif text-propertic-gold">property mortgage.</span></>}
      intro={isAR
        ? "قدّر أقساطك الشهرية، الدفعة الأولى، تكاليف التمويل، وسيناريوهات نسبة القرض إلى القيمة (LTV) لشراء عقار في الإمارات. تحوّل الأداة أدناه سعر العقار والدفعة الأولى والفائدة السنوية ومدّة القرض إلى قسط شهري دقيق بالدرهم والدولار، مع تفصيل إجمالي الفوائد على مدى القرض. الأداة توضيحية وتتيح لكم اختيار أي دفعة أولى؛ وللمرجعية، تحدّد أنظمة المصرف المركزي الإماراتي نسبة القرض القصوى بـ٨٠٪ للمقيمين على عقارات دون ٥ ملايين درهم، و٥٠٪ لغير المقيمين."
        : "Estimate monthly payments, down payment, financing costs and LTV scenarios for UAE property purchases. The calculator below turns property price, down-payment, annual interest rate and loan tenure into an accurate monthly instalment in AED and USD, with total interest broken out. The tool is illustrative and lets you model any down payment; for reference, Central Bank of the UAE (CBUAE) rules cap LTV at 80% for residents on properties under AED 5M and 50% for non-residents."}
      extraJsonLd={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebApplication",
            "name": "Dubai Mortgage Calculator",
            "applicationCategory": "FinanceApplication",
            "url": "https://thepropertic.ae/mortgage-calculator",
            "operatingSystem": "Any",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "AED" },
            "provider": { "@id": "https://thepropertic.ae/#organization" }
          },
          {
            "@type": "MortgageLoan",
            "name": "UAE Mortgage",
            "loanType": "Mortgage",
            "currency": "AED",
            "areaServed": { "@type": "Country", "name": "United Arab Emirates" },
            "provider": { "@id": "https://thepropertic.ae/#organization" },
            "requiredCollateral": "Property being financed",
            "domiciledMortgage": true,
            "interestRateType": "Variable (EIBOR-linked) or Fixed",
            "description": "UAE mortgage products regulated by the Central Bank of the UAE (CBUAE). LTV caps: UAE nationals up to 85% on a first home under AED 5M, residents up to 80%, and non-residents subject to bank-specific criteria (a 50% LTV may apply depending on the property and financing structure). Off-plan properties are capped at 50% LTV. Variable rates may reference EIBOR (published daily by the CBUAE) plus a bank margin."
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertic.ae/" },
              { "@type": "ListItem", "position": 2, "name": "Mortgage Calculator", "item": "https://thepropertic.ae/mortgage-calculator" }
            ]
          }
        ]
      }}
    >
      <MortgageCalculator onApply={(payload) => loanRef.current?.open(payload)} />
      <LoanForm ref={loanRef} />
      <RoniCTACard
        testId="mortgage-roni-cta"
        copy={{
          en: {
            eyebrow: "Ask Roni · AI Advisor",
            heading: "Need help with",
            headingAccent: "the mortgage rules?",
            body: "Need help understanding Dubai mortgage rules or your calculation? Ask Roni, The Propertic AI Advisor, for a quick, evidence-based answer.",
            cta: "Ask Roni",
          },
          ar: {
            eyebrow: "اسأل روني · مستشار ذكي",
            heading: "تحتاج مساعدة في",
            headingAccent: "قواعد الرهن العقاري؟",
            body: "تحتاج شرحاً لقواعد الرهن العقاري في دبي أو لحساباتك؟ اسأل روني، المستشار الذكي من بروبيرتك، للحصول على إجابة سريعة مبنية على البيانات.",
            cta: "اسأل روني",
          },
        }}
      />
    </LandingShell>
  );
}

// ============================================================================
// 4. /uae-mortgage , same tool, angled at UAE mortgage RULES / rates
//    Priority tiers: UAE Nationals → Residents → Overseas buyers
// ============================================================================
export function UaeMortgagePage() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const loanRef = useRef();
  return (
    <LandingShell
      wide
      showConsultation={false}
      faqVariant="mortgage"
      helmet={{
        title: isAR
          ? "دليل الرهن العقاري في الإمارات | نسب التمويل والتكاليف"
          : "UAE Mortgage Guide | Rates, LTV & Buying Costs",
        description: isAR
          ? "افهم كيف يعمل تمويل العقار في الإمارات: حدود نسبة القرض إلى القيمة، تكاليف الرهن، والاعتبارات الأساسية للمقيمين وغير المقيمين والمواطنين."
          : "Understand UAE property financing, LTV limits, mortgage costs and key considerations for residents, non-residents and UAE nationals.",
        canonical: isAR ? "https://thepropertic.ae/uae-mortgage?lang=ar" : "https://thepropertic.ae/uae-mortgage",
      }}
      kicker={isAR ? "دليل · مواطنون · مقيمون · مشترون من الخارج" : "Guide · Nationals · Residents · Overseas"}
      h1={isAR ? <>الرهن العقاري في الإمارات، <span className="font-italic-serif text-propertic-gold">من المواطن إلى المستثمر من الخارج.</span></> : <>UAE mortgages, <span className="font-italic-serif text-propertic-gold">from local to overseas.</span></>}
      intro={isAR
        ? "يُحدّد المصرف المركزي الإماراتي (CBUAE) حدود نسبة القرض إلى القيمة (LTV) بوضوح بحسب فئة المشتري. يمكن للمواطن الإماراتي اقتراض ما يصل إلى 85% للمسكن الأوّل دون 5 ملايين درهم، و75% لما فوق ذلك، و65% للعقار الثاني، مع حدّ أقصى لنسبة عبء الدين (DBR) 60%. يمكن للمقيمين في الإمارات الحاملين للهوية الإماراتية وتأشيرة إقامة سارية اقتراض ما يصل إلى 80% للمسكن الأوّل دون 5 ملايين درهم، و70% لما فوق ذلك، و60% للعقار الثاني. يمكن للمشترين من خارج الإمارات (غير المقيمين) تمويل جزء من قيمة العقار عبر البنوك المشاركة، وذلك وفقاً لمعايير كلّ بنك؛ وقد يُطبَّق حدّ تمويل بنسبة 50% حسب العقار وهيكل التمويل. تخضع العقارات قيد الإنشاء لحدّ أقصى للتمويل بنسبة 50% في كلّ الفئات. قد تعتمد أسعار المنتجات ذات السعر المتغيّر على مؤشّر إيبور (يُنشَر يومياً من قِبَل المصرف المركزي) مضافاً إليه هامش البنك. استخدم الحاسبة أدناه لتقدير قسطك الشهري بحسب فئتك."
        : "The Central Bank of the UAE (CBUAE) sets clear loan-to-value (LTV) caps by buyer tier. UAE nationals can borrow up to 85% LTV on a first home under AED 5M, 75% above, and 65% on a second property, with a maximum Debt Burden Ratio (DBR) of 60%. UAE residents holding a valid Emirates ID and residency visa can borrow up to 80% on a first home under AED 5M, 70% above, and 60% on a second property. Overseas non-resident buyers can finance a portion of property value with participating banks, subject to bank-specific criteria; a 50% LTV may apply depending on the property and financing structure. Off-plan properties are subject to a maximum LTV of 50% across all tiers. Rates on variable products can reference EIBOR (published daily by the CBUAE) plus a bank margin. Use the calculator below to project your monthly instalment based on your tier."}
      extraJsonLd={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What LTV can UAE nationals get on a mortgage?", "acceptedAnswer": { "@type": "Answer", "text": "UAE nationals can borrow up to 85% of property value for a first home or owner-occupied property valued at AED 5M or less, up to 75% where the value exceeds AED 5M, and up to 65% for a second/subsequent home or investment property, per Central Bank of the UAE (CBUAE) mortgage regulations. Off-plan properties are subject to a maximum LTV of 50%. Maximum mortgage tenure is 25 years. Maximum Debt Burden Ratio (DBR) is generally 60% for UAE nationals, subject to CBUAE rules and the bank's own underwriting criteria." } },
              { "@type": "Question", "name": "What LTV do UAE residents get?", "acceptedAnswer": { "@type": "Answer", "text": "UAE-resident expatriates can generally borrow up to 80% of property value for a first home or owner-occupied property valued at AED 5M or less, up to 70% where the value exceeds AED 5M, and up to 60% for a second/subsequent or investment property. Off-plan financing is subject to a maximum LTV of 50% under the CBUAE framework. Documentation and income requirements vary by bank." } },
              { "@type": "Question", "name": "Can overseas non-resident buyers get a UAE mortgage?", "acceptedAnswer": { "@type": "Answer", "text": "Some UAE banks offer mortgage financing to eligible non-resident foreign buyers, although availability, maximum LTV, income requirements and eligible nationalities vary by bank. A 50% LTV may apply depending on the property and financing structure. Non-resident applications generally require additional income and financial verification, and buyers should obtain a bank-specific approval before relying on any specific LTV or financing amount." } },
              { "@type": "Question", "name": "What is EIBOR and how does it affect a UAE mortgage?", "acceptedAnswer": { "@type": "Answer", "text": "EIBOR (Emirates Interbank Offered Rate) is a UAE dirham money-market reference rate published daily by the Central Bank of the UAE. Where a mortgage is priced as EIBOR plus a bank margin, changes in the applicable EIBOR rate can affect the interest rate and monthly payment at the contractual rate-reset date. The bank's margin is determined by the lender and the borrower profile rather than by CBUAE regulation." } }
            ]
          },
          {
            "@type": "HowTo",
            "name": "How to get a UAE mortgage pre-approval",
            "description": "Step-by-step pathway to secure CBUAE-compliant mortgage pre-approval in the UAE, from documentation to sale-and-purchase agreement.",
            "totalTime": "P10D",
            "estimatedCost": { "@type": "MonetaryAmount", "currency": "AED", "value": "0" },
            "step": [
              { "@type": "HowToStep", "position": 1, "name": "Gather documentation", "text": "Emirates ID (residents) or passport (non-residents), 6 months bank statements, salary certificate or trade license, and current liability letter." },
              { "@type": "HowToStep", "position": 2, "name": "Apply to a CBUAE-licensed bank", "text": "Submit the application to a CBUAE-licensed bank offering mortgage products for your buyer tier (UAE national, resident, or non-resident). Availability, maximum LTV, currencies, eligible nationalities and documentation requirements vary by bank." },
              { "@type": "HowToStep", "position": 3, "name": "Receive pre-approval letter", "text": "The bank issues a pre-approval subject to its validity period and conditions, confirming the applicable LTV cap (up to 85% for UAE nationals, 80% for residents, and up to 50% for off-plan or overseas buyers depending on the product) and an indicative EIBOR-linked or fixed rate. Pre-approval is a lender process and is not itself a DLD prerequisite for signing an SPA." },
              { "@type": "HowToStep", "position": 4, "name": "Sign SPA and pay DLD fee", "text": "Sign the Sale & Purchase Agreement with the seller, pay the 4% Dubai Land Department transfer fee, and complete mortgage registration at DLD." }
            ]
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertic.ae/" },
              { "@type": "ListItem", "position": 2, "name": "UAE Mortgage", "item": "https://thepropertic.ae/uae-mortgage" }
            ]
          }
        ]
      }}
    >
      <MortgageCalculator onApply={(payload) => loanRef.current?.open(payload)} />
      <LoanForm ref={loanRef} />
    </LandingShell>
  );
}

// ============================================================================
// 5. /dubai-recently-delivered , Handover projects
// ============================================================================
export function DubaiRecentlyDeliveredPage() {
  const { lang } = useLang();
  const isAR = lang === "ar";
  return (
    <LandingShell
      showConsultation={false}
      helmet={{
        title: isAR
          ? "العائد على العقار في دبي | مشاريع سُلِّمت وأسعارها"
          : "Dubai Property ROI | Recently Delivered Projects & Prices",
        description: isAR
          ? "شاهد كيف انتقلت مشاريع مختارة سُلِّمت في دبي من سعر الإطلاق إلى السعر الحالي، مع حسابات شفّافة وسياق تفصيلي لكل مشروع."
          : "See how selected recently delivered Dubai projects have moved from launch to current prices, with transparent calculations and project-level context.",
        canonical: isAR ? "https://thepropertic.ae/dubai-recently-delivered?lang=ar" : "https://thepropertic.ae/dubai-recently-delivered",
      }}
      kicker={isAR ? "التسليم · موثّق من الجهات الرسمية" : "Handovers · DLD-verified"}
      h1={isAR ? <>ماذا حدث فعلاً <span className="font-italic-serif text-propertic-gold">لمشاريع دبي المُسلَّمة مؤخّراً.</span></> : <>See what happened to <span className="font-italic-serif text-propertic-gold">recently delivered Dubai projects.</span></>}
      intro={isAR
        ? "لا نعرض قوائم للمشاريع القادمة. نعرض ما سُلِّم بالفعل, عشرة مشاريع رئيسية عبر إعمار، ميراس ودامَاك، بتواريخ إطلاق وتسليم موثّقة من سجلات إتمام دائرة الأراضي والأملاك. اضغط على أي بطاقة لرؤية المطوّر، التاريخ، والارتفاع الإرشادي في القيمة."
        : "We don't peddle upcoming project lists. We show what actually delivered, ten flagship developments across Emaar, Meraas and Damac, with launch and handover dates verified against Dubai Land Department completion registrations. Tap any card to see developer, dates and indicative appreciation."}
      extraJsonLd={{
        "@context": "https://schema.org", "@type": "CollectionPage",
        "name": "Dubai Recently Delivered Projects",
        "url": "https://thepropertic.ae/dubai-recently-delivered"
      }}
    >
      <HandoverCollection />
    </LandingShell>
  );
}
